import { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useChatContext } from '../context/ChatContext.jsx'
import ConsentModal from './ConsentModal.jsx'
import HitlForm from './HitlForm.jsx'
import NavPrompt from './NavPrompt.jsx'
import { useConsent } from '../hooks/useConsent.js'

const panelVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.96 },
  visible: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 40, scale: 0.96 },
}

const panelTransition = {
  duration: 0.3,
  ease: [0.25, 0.1, 0.25, 1],
}

const messageVariants = {
  hidden: { opacity: 0, y: 10, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1 },
}

function ChatPanel() {
  const { isOpen, closeChat, messages, isLoading, sendMessage, suggestions, headline, submitFeedback, activeForm, submitForm, hideForm, navDecidedIds, navAlwaysAllow } = useChatContext()
  const { consentGiven, loading: consentLoading, giveConsent } = useConsent()
  const [input, setInput] = useState('')
  const [correctionId, setCorrectionId] = useState(null)
  const [correctionText, setCorrectionText] = useState('')
  const scrollRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, isLoading])

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Escape key collapses to bubble
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        e.stopPropagation()
        closeChat()
      }
    }
    if (isOpen) {
      window.addEventListener('keydown', handleKey)
      return () => window.removeEventListener('keydown', handleKey)
    }
  }, [isOpen, closeChat])

  const handleSend = (e) => {
    e.preventDefault()
    if (!input.trim() || isLoading || !consentGiven) return
    sendMessage(input.trim())
    setInput('')
  }

  const handleSuggestion = (suggestion) => {
    if (isLoading || !consentGiven) return
    sendMessage(suggestion)
  }

  const handleThumb = (msgId, thumb) => {
    if (thumb === 'down') {
      setCorrectionId(msgId)
      setCorrectionText('')
    } else {
      submitFeedback(msgId, thumb)
      setCorrectionId(null)
    }
  }

  const handleSubmitCorrection = () => {
    if (correctionId && correctionText.trim()) {
      submitFeedback(correctionId, 'down', correctionText.trim())
      setCorrectionId(null)
      setCorrectionText('')
    }
  }

  const hasStartedConversation = messages.length > 0

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          variants={panelVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={panelTransition}
          className="fixed inset-4 sm:top-6 sm:right-6 sm:bottom-6 sm:left-auto sm:w-[380px] sm:max-w-[calc(100vw-48px)] z-50 bg-soft-surface border border-soft-rule shadow-[0_24px_48px_rgba(0,0,0,0.08)] flex flex-col overflow-hidden chat-panel"
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-soft-rule flex justify-between items-center shrink-0">
            <span className="text-sm font-medium text-soft-ink">Ask Mugunth</span>
            <button
              onClick={closeChat}
              className="text-soft-dim hover:text-soft-ink cursor-pointer text-base leading-none"
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-4">
            {consentGiven === false && !consentLoading && (
              <ConsentModal
                onAccept={async () => {
                  const ok = await giveConsent()
                  if (!ok) {}
                }}
                onDecline={closeChat}
              />
            )}

            {!hasStartedConversation && (
              <>
                <p className="text-[13px] text-soft-dim">{headline}</p>
                <p className="text-sm text-soft-ink">
                  I'm an agent trained on Mugunth's CV, blog posts, and a few hundred Q&A pairs he wrote himself. Try one of these - or ask anything.
                </p>
                <div className="flex flex-col">
                  {suggestions.map((s, i) => (
                    <button
                      key={s}
                      onClick={() => handleSuggestion(s)}
                      className={`text-left text-sm text-soft-ink py-3 flex justify-between items-center cursor-pointer hover:text-soft-dim transition-colors ${
                        i > 0 ? 'border-t border-soft-rule' : ''
                      }`}
                    >
                      <span>{s}</span>
                      <span className="text-soft-faint text-xs">↗</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            <AnimatePresence initial={false}>
              {messages.map((msg, i) => (
                <motion.div
                  key={msg.id}
                  variants={messageVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ duration: 0.25, delay: i * 0.04, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  <div
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl ${
                        msg.role === 'user'
                          ? 'bg-soft-ink text-soft-bg rounded-br-md'
                          : 'bg-soft-hi text-soft-ink rounded-bl-md'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>

                  {msg.role === 'assistant' && (
                    <div className="flex gap-3 mt-1.5 ml-1">
                      <button
                        onClick={() => handleThumb(msg.id, 'up')}
                        className="text-xs text-soft-dim hover:text-soft-ink cursor-pointer"
                        aria-label="Thumbs up"
                      >
                        👍
                      </button>
                      <button
                        onClick={() => handleThumb(msg.id, 'down')}
                        className="text-xs text-soft-dim hover:text-soft-ink cursor-pointer"
                        aria-label="Thumbs down"
                      >
                        👎
                      </button>
                      <button
                        onClick={() => { setCorrectionId(msg.id); setCorrectionText('') }}
                        className="text-xs text-soft-dim hover:text-soft-ink cursor-pointer"
                      >
                        Add correction
                      </button>
                    </div>
                  )}

                  {correctionId === msg.id && (
                    <div className="mt-2 ml-1">
                      <textarea
                        value={correctionText}
                        onChange={(e) => setCorrectionText(e.target.value)}
                        placeholder="What should the answer have been?"
                        className="w-full px-3 py-2 bg-soft-bg border border-soft-rule text-xs resize-none"
                        rows={2}
                      />
                      <button
                        onClick={handleSubmitCorrection}
                        className="mt-2 px-3 py-1.5 bg-soft-ink text-soft-bg text-xs cursor-pointer"
                      >
                        Submit correction
                      </button>
                    </div>
                  )}

                  {msg.role === 'assistant' && msg.returned_path && !navAlwaysAllow && !navDecidedIds.includes(msg.id) && (
                    <NavPrompt message={msg} />
                  )}
                </motion.div>
              ))}
            </AnimatePresence>

            {isLoading && (
              <div className="flex justify-start gap-1">
                <span className="w-1.5 h-1.5 bg-soft-dim rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-soft-dim rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-soft-dim rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            )}

            {activeForm && (
              <HitlForm
                schema={activeForm}
                onSubmit={submitForm}
                onCancel={hideForm}
              />
            )}
          </div>

          {/* Composer */}
          <form onSubmit={handleSend} className="px-5 py-4 border-t border-soft-rule shrink-0 flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask something…"
              className="flex-1 bg-transparent border-none text-sm outline-none focus-visible:ring-0 focus-visible:shadow-none placeholder:text-soft-faint"
              disabled={isLoading || !consentGiven || consentLoading}
            />
            <button
              type="submit"
              disabled={!input.trim() || isLoading || !consentGiven || consentLoading}
              className="w-7 h-7 bg-soft-ink text-soft-bg flex items-center justify-center text-xs cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Send message"
            >
              ↑
            </button>
          </form>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ChatPanel
